"use memo"

import { ReactNode, useRef, useState } from 'react'
import { Images } from '@urbanx/agx-ui-components'
import useOutsideAlert from 'hooks/useOutsideAlert'
import './ListItemOptions.scss'

export interface ListItemOption<TAction extends string> {
  action: TAction
  children: ReactNode
}

interface ListItemOptionsProps<TAction extends string> {
  actions: ListItemOption<TAction>[]
  id: string
  onAction(action: TAction): void
  dataTestId?: string
  trigger?: ReactNode
}

const ListItemOptions = <TAction extends string>({
  actions,
  id,
  onAction,
  dataTestId,
  trigger,
}: ListItemOptionsProps<TAction>) => {
  const [isOpen, setIsOpen] = useState(false)
  const moreOptionsRef = useRef(null)

  useOutsideAlert(moreOptionsRef, (onTriggerOutside: string) => {
    if (onTriggerOutside === 'outside') setIsOpen(false)
  })

  if (actions.length === 0) return null

  return (
    <div
      onClick={(event) => {
        event.stopPropagation()
        setIsOpen(!isOpen)
      }}
      className="listItemOptions"
      ref={moreOptionsRef}
      data-testid={dataTestId}
    >
      {trigger ?? <Images.More />}
      {isOpen && (
        <div className="listItemOptions__menu" id={id}>
          {actions.map((option) => (
            <div className="listItemOptions__item" key={option.action}>
              <button
                type="button"
                data-testid={`${dataTestId}-${option.action.toLowerCase()}`}
                onClick={(event) => {
                  event.stopPropagation()
                  onAction(option.action)
                  setIsOpen(false)
                }}
              >
                {option.children}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default ListItemOptions
